/**
 * The one routine behind subsets *and* fish.
 *
 * Andrew Stuart's observation, and the reason this file exists: naked subsets,
 * hidden subsets and X-Wing/Swordfish/Jellyfish are all the same deduction --
 * "n things are confined to n slots, so those slots are used up" -- seen from
 * three different angles.
 *
 *   naked subset   items = cells in a unit    slots = digits
 *   hidden subset  items = digits in a unit   slots = positions in that unit
 *   fish           items = base lines         slots = crossing lines
 *
 * Write it once here, and each technique only has to say what its items and
 * slots are and what the conclusion eliminates.
 */

/** An item and the slots it may occupy, as a bitmask. */
export interface Confinable<T> {
  readonly ref: T;
  readonly slots: number;
}

export interface ConfinedSet<T> {
  readonly items: readonly T[];
  /** Union of the chosen items' slots -- exactly `items.length` bits. */
  readonly slots: number;
}

function popcount(mask: number): number {
  let n = 0;
  let m = mask;
  while (m) {
    m &= m - 1;
    n++;
  }
  return n;
}

/**
 * Every combination of `size` items whose slots union to exactly `size` bits,
 * in index order. Items with more than `size` slots can never take part, so
 * they are filtered out before the search -- that is what keeps this cheap.
 *
 * `onFound` returning true stops the search (callers want the first usable hit,
 * and "usable" depends on whether the conclusion actually eliminates anything).
 */
export function forEachConfinedSet<T>(
  candidates: readonly Confinable<T>[],
  size: number,
  onFound: (found: ConfinedSet<T>) => boolean,
): void {
  const usable = candidates.filter(
    (candidate) => candidate.slots !== 0 && popcount(candidate.slots) <= size,
  );
  if (usable.length < size) return;

  const chosen: number[] = [];

  const recurse = (start: number, slots: number): boolean => {
    if (chosen.length === size) {
      if (popcount(slots) !== size) return false;
      return onFound({ items: chosen.map((i) => usable[i]!.ref), slots });
    }
    // Prune: even taking every remaining item cannot reach `size` picks.
    for (let i = start; i <= usable.length - (size - chosen.length); i++) {
      const merged = slots | usable[i]!.slots;
      // A union already wider than `size` can only grow -- abandon this branch.
      if (popcount(merged) > size) continue;
      chosen.push(i);
      if (recurse(i + 1, merged)) return true;
      chosen.pop();
    }
    return false;
  };

  recurse(0, 0);
}
